import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';
import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { Box, Flex } from '@xingternal/winery-ui/lib/UI';
import { Icon } from '@xingternal/winery-ui/lib/Icon';

const Overlay = styled(Flex).attrs({
  alignItems: 'center',
  justifyContent: 'center',
})`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: ${themeGet('zIndices.tooltipLevel')};
`;

const Container = styled(Box).attrs({
  mx: 2,
  p: 3,
  boxShadow: 2,
  borderRadius: 1,
})`
  background-color: white;
  position: absolute;
  max-height: 500px;
  overflow: auto;
`;

const ICON_TOP_SPACING = 8;

const IconWrapper = styled.div<{ scrollHeight: string }>`
  height: ${({ scrollHeight }) => scrollHeight};
  position: absolute;
  right: ${ICON_TOP_SPACING}px;
  top: ${themeGet('space.2')};
`;

const CloseIcon = styled(Icon).attrs({
  name: 'ShapeCross',
  color: 'grey400',
  width: 20,
  height: 20,
})`
  background-color: white;
  cursor: pointer;
  position: sticky;
  top: 0px;
`;

type MobileInfolayerProps = {
  className?: string;
  content: React.ReactNode;
  onClose: () => void;
  showMobileInfolayer: boolean;
  showCloseIcon?: boolean;
};

const MobileInfolayer = ({
  content,
  className,
  onClose,
  showMobileInfolayer,
  showCloseIcon,
}: MobileInfolayerProps) => {
  const infolayerEL = useRef(null);
  const [showInfolayer, toggleInfolayer] =
    useState<boolean>(showMobileInfolayer);
  const [containerScrollHeight, setContainerScrollHeight] =
    useState<string>('auto');

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    !infolayerEL.current?.contains(event.target) && onClose();
  }, []);

  const handleMouseDownListener = (isMobileInfolayerVisible: boolean) => {
    if (isMobileInfolayerVisible) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }
  };

  useEffect(() => {
    toggleInfolayer(showMobileInfolayer);
    handleMouseDownListener(showMobileInfolayer);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showMobileInfolayer]);

  useLayoutEffect(() => {
    const height = infolayerEL.current
      ? `${infolayerEL.current?.scrollHeight - ICON_TOP_SPACING}px`
      : 'auto';

    setContainerScrollHeight(height);
  });

  return showInfolayer ? (
    <Overlay data-testid="mobile-infolayer-overlay">
      <Container
        data-testid="mobile-infolayer"
        className={className}
        ref={infolayerEL}
      >
        {content}
        {showCloseIcon && (
          <IconWrapper scrollHeight={containerScrollHeight}>
            <CloseIcon onClick={onClose} />
          </IconWrapper>
        )}
      </Container>
    </Overlay>
  ) : null;
};

export { MobileInfolayer };
